const euroFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const euroDetailedFmt = euroFmt;

const pctFmt = new Intl.NumberFormat('de-DE', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Format absolute percentage points (e.g. 12.5 → "12,5 %"). */
export function formatPercentPoints(value: number): string {
  return `${pctFmt.format(value / 100)}`;
}

export function formatEuro(value: number, detailed = false): string {
  if (!Number.isFinite(value)) return '—';
  return detailed ? euroDetailedFmt.format(value) : euroFmt.format(value);
}

export function formatEuroOptional(value: number | undefined | null, detailed = true): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return '—';
  return formatEuro(value, detailed);
}

export function formatSignedEuro(value: number): string {
  const abs = formatEuro(Math.abs(value), true);
  if (value > 0) return `+${abs}`;
  if (value < 0) return `−${abs}`;
  return formatEuro(0);
}

const moneyFmtCache = new Map<string, Intl.NumberFormat>();

function moneyFormatter(currency: string): Intl.NumberFormat {
  const code = currency || 'EUR';
  const cached = moneyFmtCache.get(code);
  if (cached) return cached;
  let fmt: Intl.NumberFormat;
  try {
    fmt = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } catch {
    fmt = euroFmt;
  }
  moneyFmtCache.set(code, fmt);
  return fmt;
}

/** Format a monetary value in the given ISO 4217 currency (defaults to EUR). */
export function formatMoney(value: number, currency: string = 'EUR'): string {
  if (!Number.isFinite(value)) return '—';
  return moneyFormatter(currency).format(value);
}

/** Like formatMoney but with an explicit +/− prefix (zero is unsigned). */
export function formatSignedMoney(value: number, currency: string = 'EUR'): string {
  if (!Number.isFinite(value)) return '—';
  const abs = moneyFormatter(currency).format(Math.abs(value));
  if (value > 0) return `+${abs}`;
  if (value < 0) return `−${abs}`;
  return moneyFormatter(currency).format(0);
}

/** Portfolio multiple, e.g. 1.4 → "1,4×". */
export function formatMultiple(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)}×`;
}

export function formatNumberCompact(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('de-DE', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

const monthYearFmt = new Intl.DateTimeFormat('de-DE', { month: 'short', year: 'numeric' });
const dateLongFmt = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long' });
const dateShortFmt = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' });

/** "Apr 2024" style label for a stored ISO timestamp; "—" when empty/invalid. */
export function formatMonthYear(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return monthYearFmt.format(d);
}

/** "15. März 2022" style label for a stored ISO timestamp; "—" when empty/invalid. */
export function formatDateLong(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return dateLongFmt.format(d);
}

/** "15.03.22" style label for a stored ISO timestamp; "—" when empty/invalid. */
export function formatDateShort(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return dateShortFmt.format(d);
}

/**
 * Months of runway from cash and monthly burn.
 * Returns `Infinity` when burn ≤ 0 (no cash drain). Returns `undefined`
 * when either input is missing/invalid or cash is negative.
 */
export function computeRunwayMonths(
  remainingCash: number | undefined,
  burn: number | undefined
): number | undefined {
  if (remainingCash == null || !Number.isFinite(remainingCash) || remainingCash < 0) return undefined;
  if (burn == null || !Number.isFinite(burn)) return undefined;
  if (burn <= 0) return Number.POSITIVE_INFINITY;
  return remainingCash / burn;
}

/**
 * Human-readable runway label:
 * - "—" if undefined (missing inputs)
 * - "Profitable" when burn ≤ 0
 * - "X mo" below 12 months, otherwise "X.X yr"
 */
export function formatRunway(months: number | undefined): string {
  if (months === undefined) return '—';
  if (!Number.isFinite(months)) return 'Profitable';
  if (months < 12) {
    const m = Math.max(0, Math.round(months * 10) / 10);
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)} mo`;
  }
  return `${(months / 12).toFixed(1)} yr`;
}

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

/**
 * Holding period label measured from `iso` to now:
 * - "—" if missing/invalid/future
 * - "X mo" when below 1 year
 * - "X.X yr" otherwise
 */
export function formatHoldingPeriod(iso: string | undefined): string {
  if (!iso) return '—';
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return '—';
  const diffMs = Date.now() - start.getTime();
  if (diffMs < 0) return '—';
  const years = diffMs / MS_PER_YEAR;
  if (years < 1) {
    const months = Math.max(1, Math.round(years * 12));
    return `${months} mo`;
  }
  return `${years.toFixed(1)} yr`;
}
